<script setup lang="ts">
import { sanitizeUrl } from '@braintree/sanitize-url'
import { computed, useSlots, type PropType } from 'vue'
import type { RouteLocationRaw, RouterLinkProps } from 'vue-router'
import type { UnionFromTuple } from '@oen.web.vue/utils'
import { analyticsNameProp } from '../common/props'
import { themes } from './variants'

type Theme = UnionFromTuple<typeof themes>

const props = defineProps({
  activeClass: {
    type: String,
    required: false,
    default: undefined,
  },
  ariaCurrentValue: {
    type: String as PropType<RouterLinkProps['ariaCurrentValue']>,
    required: false,
    default: undefined,
  },
  exactActiveClass: {
    type: String,
    required: false,
    default: undefined,
  },
  /** Calls router.replace instead of router.push */
  replace: {
    type: Boolean,
    required: false,
    default: false,
  },
  href: {
    type: String,
    default: null,
  },
  to: {
    type: [String, Object] as PropType<RouteLocationRaw>,
    required: false,
    default: undefined,
  },
  target: {
    type: String,
    default: null,
    validator: (value) => {
      return value !== '_blank'
    },
  },
  theme: {
    type: String as PropType<Theme>,
    default: 'primary',
    validator: (theme: Theme) => themes.includes(theme),
  },
  ...analyticsNameProp,
})

interface Emits {
  (e: 'click', v: MouseEvent): void
}

const emits = defineEmits<Emits>()

const slots = useSlots()

const classes = computed(() => {
  return {
    /* Apply styling so icons are aligned correctly while not preventing wrapping on long text links without icons */
    'oen-ui-link--has-icon': slots.leftIcon || slots.rightIcon,
    'oen-ui-link--secondary': props.theme === 'secondary',
  }
})

const htmlEle = computed(() => {
  if (props.href) {
    return 'a'
  } else {
    return 'button'
  }
})

const rel = computed(() => {
  if (props.href) {
    return 'nofollow noreferrer noopener'
  } else {
    return null
  }
})
const localHref = computed(() => {
  if (props.href) {
    return sanitizeUrl(props.href)
  } else {
    return null
  }
})
const localTarget = computed(() => {
  if (props.href) {
    return props.target
  } else {
    return null
  }
})
const type = computed(() => {
  if (props.href) {
    return null
  } else {
    return 'button'
  }
})
</script>

<template>
  <RouterLink
    v-if="to"
    v-slot="{ isActive, isExactActive, href: hrefSlot, navigate }"
    :to="to"
    :active-class="activeClass"
    :aria-current-value="ariaCurrentValue"
    :custom="true"
    :exact-active-class="exactActiveClass"
    :replace="replace"
  >
    <a
      v-bind="$attrs"
      :href="sanitizeUrl(hrefSlot)"
      :analytics-name="analyticsName"
      :class="[
        isActive ? activeClass : '',
        isExactActive ? exactActiveClass : '',
        'oen-ui-link',
        classes,
      ]"
      @click="navigate"
    >
      <div v-if="slots.leftIcon" class="oen-ui-link-left-icon">
        <!-- @slot For the left Icon -->
        <slot name="leftIcon" />
      </div>
      <span class="oen-ui-link-text">
        <!-- @slot link content -->
        <slot />
      </span>
      <div v-if="slots.rightIcon" class="oen-ui-link-right-icon">
        <!-- @slot For the right Icon -->
        <slot name="rightIcon" />
      </div>
    </a>
  </RouterLink>
  <Component
    :is="htmlEle"
    v-else
    :href="localHref"
    :rel="rel"
    :target="localTarget"
    :type="type"
    :analytics-name="analyticsName"
    class="oen-ui-link"
    :class="classes"
    @click="emits('click', $event)"
  >
    <div v-if="slots.leftIcon" class="oen-ui-link-left-icon">
      <!-- @slot leftIcon: For the left Icon -->
      <slot name="leftIcon" />
    </div>
    <!-- @slot default: link content -->
    <span class="oen-ui-link-text">
      <slot />
    </span>
    <div v-if="slots.rightIcon" class="oen-ui-link-right-icon">
      <!-- @slot rightIcon: For the right Icon -->
      <slot name="rightIcon" />
    </div>
  </Component>
</template>

<style scoped>
.oen-ui-link {
  word-break: break-word;
  @apply text-oen-ui-primary font-bold;
  &:hover {
    @apply underline;
  }
  &:active {
    @apply text-oen-ui-primary-button-pressed;
  }

  &.oen-ui-link--secondary {
    @apply text-secondary-text;
    &:active {
      @apply text-tertiary-text;
    }
  }
  /* Apply styling so icons are aligned correctly while not not preventing wrapping on long text links without icons */
  &.oen-ui-link--has-icon {
    @apply inline-flex items-center align-top;
  }
  .oen-ui-link-left-icon,
  .oen-ui-link-right-icon {
    @apply inline-flex items-center align-middle;
  }
  .oen-ui-link-left-icon {
    @apply me-1_5;
  }
  .oen-ui-link-right-icon {
    @apply ms-1_5;
  }
}
</style>
